import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { closeOutline, imagesOutline, trashOutline } from 'ionicons/icons'
import { useEffect, useRef, useState } from 'react'
import { parseDatetime } from '../helpers/datetime'

export type ViewerPhoto = {
  clientId: string
  url: string
  uploadedByName?: string | null
  takenAt?: string | null
  createdAt?: string | null
  /** Queued photo that exhausted its retries; can be deleted from the device. */
  discardable?: boolean
}

type OrderPhotoViewerModalProps = {
  photos: readonly ViewerPhoto[]
  startIndex: number
  onDismiss: () => void
  onDiscard?: (clientId: string) => void
}

/** Full-screen swipeable photo viewer (horizontal scroll-snap). */
export const OrderPhotoViewerModal = ({
  photos,
  startIndex,
  onDismiss,
  onDiscard,
}: OrderPhotoViewerModalProps) => {
  const { _ } = useLingui()
  const [presentAlert] = useIonAlert()
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const stripRef = useRef<HTMLDivElement>(null)
  // The tapped slot is a mount-time value: the parent recomputes startIndex
  // when the list reorders under the open modal, and that must not yank the
  // strip back to the originally tapped photo.
  const initialIndex = useRef(startIndex)

  // Jump to the tapped photo once the strip has real dimensions — inside an
  // IonModal the content measures 0 until the modal is presented, so a bare
  // scrollLeft on mount silently lands on the first slide.
  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const jumpToStart = () => {
      strip.scrollLeft = strip.clientWidth * initialIndex.current
    }
    if (strip.clientWidth > 0) {
      jumpToStart()
      return
    }
    const observer = new ResizeObserver(() => {
      if (strip.clientWidth === 0) return
      jumpToStart()
      observer.disconnect()
    })
    observer.observe(strip)
    return () => observer.disconnect()
  }, [])

  const handleScroll = () => {
    const strip = stripRef.current
    if (!strip || strip.clientWidth === 0) return
    setCurrentIndex(Math.round(strip.scrollLeft / strip.clientWidth))
  }

  // Touch swipes ride the native scroll-snap; mouse drags don't scroll a snap
  // container, so emulate them: drag freely (snap off), snap to the nearest
  // slide on release.
  const mouseDrag = useRef<{ startX: number; startScroll: number } | null>(null)
  const [dragging, setDragging] = useState(false)

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return
    const strip = stripRef.current
    if (!strip) return
    mouseDrag.current = { startX: event.clientX, startScroll: strip.scrollLeft }
    setDragging(true)
    strip.setPointerCapture?.(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const strip = stripRef.current
    if (!strip || !mouseDrag.current) return
    strip.scrollLeft =
      mouseDrag.current.startScroll - (event.clientX - mouseDrag.current.startX)
  }

  const handlePointerEnd = () => {
    const strip = stripRef.current
    if (!strip || !mouseDrag.current) return
    mouseDrag.current = null
    setDragging(false)
    if (strip.clientWidth === 0) return
    const target =
      Math.round(strip.scrollLeft / strip.clientWidth) * strip.clientWidth
    if (!strip.scrollTo) {
      strip.scrollLeft = target
      return
    }
    strip.scrollTo({ left: target, behavior: 'smooth' })
  }

  const current = photos[currentIndex]
  // Prefer the real capture time; fall back to when the row was recorded
  const timestamp = current?.takenAt ?? current?.createdAt
  const takenLabel = timestamp
    ? parseDatetime(timestamp)?.toLocaleString({
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null

  const confirmDiscard = () => {
    if (!current || !onDiscard) return
    const { clientId } = current
    presentAlert({
      header: _(msg`Discard this photo?`),
      message: _(msg`It never uploaded and will be deleted from this device.`),
      buttons: [
        { text: _(msg`Cancel`), role: 'cancel' },
        {
          text: _(msg`Discard`),
          role: 'destructive',
          handler: () => onDiscard(clientId),
        },
      ],
    })
  }

  return (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar>
          <IonTitle>
            {currentIndex + 1} / {photos.length}
          </IonTitle>
          <IonButtons slot='end'>
            {current?.discardable && onDiscard && (
              <IonButton
                color='danger'
                onClick={confirmDiscard}
                aria-label={_(msg`Discard photo`)}
              >
                <IonIcon icon={trashOutline} />
              </IonButton>
            )}
            <IonButton onClick={onDismiss} aria-label={_(msg`Close`)}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div
          className={`photo-viewer__strip ${
            dragging ? 'photo-viewer__strip--dragging' : ''
          }`}
          ref={stripRef}
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        >
          {photos.map((photo) => (
            <div key={photo.clientId} className='photo-viewer__slide'>
              {photo.url ? (
                <img src={photo.url} alt='' draggable={false} />
              ) : (
                // Preview lost from disk (a failed queued photo can still be
                // opened here to discard it)
                <span className='photo-viewer__missing'>
                  <IonIcon icon={imagesOutline} />
                </span>
              )}
            </div>
          ))}
        </div>
        {(current?.uploadedByName || takenLabel) && (
          <div className='photo-viewer__caption'>
            {[current?.uploadedByName, takenLabel].filter(Boolean).join(' · ')}
          </div>
        )}
      </IonContent>
    </>
  )
}
